import React from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'

import './AdminSidebar.scss'

import { useAuth } from '../../../Components/AuthProvider/AuthProvider'
import logoWordmark from '../../Assets/Icon/AdminDashboard/mn9vwciu-ti9yp4r.svg'
import logoMark from '../../Assets/Icon/AdminDashboard/mn9vwciu-0bllthn.svg'
import iconHome from '../../Assets/Icon/AdminDashboard/mn9vwcis-lofx90g.svg'
import iconMenu from '../../Assets/Icon/AdminDashboard/mn9vwcit-y0ixzkl.svg'
import iconProfile from '../../Assets/Icon/AdminDashboard/mn9vwcis-f0htsww.svg'
import iconChevronDown from '../../Assets/Icon/AdminDashboard/mn9vwcis-xzgowe7.svg'

export type AdminSidebarActiveKey =
  | 'dashboard'
  | 'kyc-queue'
  | 'investors'
  | 'traders-card'
  | 'token-assets'
  | 'pool-state'
  | 'notifications'

type SidebarItemProps = {
  itemKey: AdminSidebarActiveKey
  label: string
  icon: string
  to: string
  activeKey: AdminSidebarActiveKey
  onNavigate?: () => void
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  itemKey,
  label,
  icon,
  to,
  activeKey,
  onNavigate,
}) => (
  <Link
    to={to}
    className={['admin-sidebar__item', itemKey === activeKey ? 'admin-sidebar__item--active' : '']
      .filter(Boolean)
      .join(' ')}
    onClick={() => onNavigate?.()}
  >
    <img src={icon} alt='' className='admin-sidebar__item-icon' />
    <span className='admin-sidebar__item-label'>{label}</span>
  </Link>
)

type Props = {
  activeKey?: AdminSidebarActiveKey
  variant?: 'static' | 'offcanvas'
  onNavigate?: () => void
}

const AdminSidebar: React.FC<Props> = ({
  activeKey = 'dashboard',
  variant = 'static',
  onNavigate,
}) => {
  const { user, logout } = useAuth()
  const [isSwitching, setIsSwitching] = React.useState(false)
  const navigate = useNavigate()
  const isDashboardActive = activeKey === 'dashboard'

  const toForKey = (key: AdminSidebarActiveKey) => {
    switch (key) {
      case 'dashboard':
        return '/blockchain/dashboard'
      case 'kyc-queue':
        return '/blockchain/kyc-queue/pending'
      case 'investors':
        return '/blockchain/investors/all'
      case 'traders-card':
        return '/blockchain/traders-card/ALL'
      case 'token-assets':
        return '/blockchain/token-assets/ACTIVE'
      case 'pool-state':
        return '/blockchain/pool-state/assigned'
      // case 'lease-assets':
      //   return '/blockchain/lease-assets'
      case 'notifications':
        return '/blockchain/notifications'
      default:
        return '/blockchain/dashboard'
    }
  }

  return (
    <div
      className={['admin-sidebar', variant === 'offcanvas' ? 'admin-sidebar--offcanvas' : '']
        .filter(Boolean)
        .join(' ')}
    >
      <div className='admin-sidebar__brand'>
        <div className='admin-sidebar__brand-row'>
          <svg
            width='164'
            height='42'
            viewBox='0 0 164 42'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M2.55428 7.2748H8.19023e-08L0 5.38873H2.55428V0.586891H6.31848V5.38873H10.486V7.2748H6.31848V14.2802C6.31848 14.9627 6.39019 15.5465 6.53358 16.0314C6.69489 16.4985 6.90104 16.8847 7.15199 17.19C7.42085 17.4775 7.72557 17.693 8.06614 17.8367C8.40672 17.9804 8.76521 18.0522 9.14159 18.0522C9.67938 18.0522 10.1992 17.9714 10.7011 17.8098C11.203 17.6302 11.6242 17.4415 11.9648 17.2439L12.7714 18.9953C12.2336 19.2647 11.6153 19.4893 10.9162 19.6689C10.2351 19.8485 9.5539 19.9383 8.87276 19.9383C6.84726 19.9383 5.2878 19.4354 4.1944 18.4295C3.10098 17.4236 2.55428 16.0404 2.55428 14.2802V7.2748ZM25.5396 7.2748H22.3131C21.0943 7.2748 20.1622 7.60711 19.5169 8.2717C18.8716 8.91835 18.5489 9.88837 18.5489 11.1817V19.3994H14.7847V11.1817C14.7847 9.34946 15.3583 7.93043 16.5055 6.92452C17.6707 5.90067 19.3376 5.38873 21.5066 5.38873H25.5396V7.2748ZM33.0501 17.5134C30.9888 17.5134 29.9582 16.6601 29.9582 14.9537C29.9582 13.2473 30.9888 12.3941 33.0501 12.3941H36.8144V17.5134H33.0501ZM35.7926 7.65199C36.4738 8.26272 36.8144 9.21468 36.8144 10.5081H33.0501C30.6124 10.5081 28.811 10.8762 27.6458 11.6127C26.4986 12.3492 25.9251 13.4628 25.9251 14.9537C25.9251 16.4447 26.4986 17.5583 27.6458 18.2948C28.811 19.0312 30.6124 19.3994 33.0501 19.3994H40.5786V10.5081C40.5786 9.66381 40.4172 8.90038 40.0946 8.21782C39.7898 7.51727 39.3329 6.91553 38.7233 6.41258C38.114 5.90965 37.3701 5.52345 36.4916 5.25402C35.6133 4.98459 34.6006 4.84987 33.4534 4.84987C33.0232 4.84987 32.5573 4.88578 32.0553 4.95763C31.5713 5.02947 31.0785 5.13727 30.5765 5.28096C30.0747 5.4067 29.5817 5.56836 29.0977 5.76595C28.6318 5.96352 28.2014 6.19704 27.8072 6.46647L28.7483 8.08311C29.3577 7.66996 30.0476 7.34663 30.8186 7.11313C31.5894 6.86166 32.3781 6.73591 33.1847 6.73591C34.2602 6.73591 35.1294 7.04127 35.7926 7.65199ZM55.0851 7.2748H51.7242C51.1147 7.2748 50.5502 7.37357 50.0304 7.57117C49.5283 7.7508 49.0891 8.04718 48.7127 8.46031C48.3543 8.87346 48.0676 9.40335 47.8523 10.05C47.6553 10.6966 47.5565 11.478 47.5565 12.3941C47.5565 13.3101 47.6553 14.0915 47.8523 14.7382C48.0676 15.3848 48.3543 15.9147 48.7127 16.3279C49.0891 16.741 49.5283 17.0464 50.0304 17.2439C50.5502 17.4236 51.1147 17.5134 51.7242 17.5134H55.0851V7.2748ZM51.7242 19.3994C50.4872 19.3994 49.3581 19.2378 48.3363 18.9145C47.3325 18.5911 46.4723 18.1241 45.7552 17.5134C45.0381 16.9027 44.4824 16.1662 44.0881 15.304C43.7117 14.4418 43.5234 13.4718 43.5234 12.3941C43.5234 11.3164 43.7117 10.3464 44.0881 9.48414C44.4824 8.62197 45.0381 7.88551 45.7552 7.2748C46.4723 6.66407 47.3325 6.19704 48.3363 5.87372C49.3581 5.55039 50.4872 5.38873 51.7242 5.38873H55.0851V0.538874H58.8492V19.3994H51.7242ZM73.1146 17.5942C72.362 17.8995 71.5464 18.0522 70.6679 18.0522C69.4312 18.0522 68.4006 17.729 67.576 17.0823C66.7694 16.4357 66.2584 15.3669 66.0433 13.876H77.5242V11.9899C77.5242 10.8762 77.3182 9.87937 76.9059 8.99919C76.4936 8.11902 75.9379 7.37357 75.2388 6.76286C74.5398 6.13416 73.7153 5.65816 72.7652 5.33484C71.8151 5.01154 70.8025 4.84987 69.727 4.84987C68.5798 4.84987 67.5311 5.0205 66.5812 5.36178C65.631 5.68511 64.8067 6.1701 64.1076 6.81675C63.4264 7.44544 62.8886 8.23579 62.4943 9.18778C62.1178 10.1218 61.9296 11.1906 61.9296 12.3941C61.9296 13.6155 62.1269 14.6932 62.5211 15.6273C62.9334 16.5613 63.5071 17.3517 64.242 17.9984C64.9769 18.627 65.8642 19.1031 66.9038 19.4264C67.9612 19.7677 69.1264 19.9383 70.3992 19.9383C70.9727 19.9383 71.5464 19.8844 72.1199 19.7766C72.6936 19.6869 73.2402 19.5611 73.7601 19.3994C74.2977 19.2198 74.7908 19.0043 75.2388 18.7528C75.7048 18.5013 76.108 18.2229 76.4487 17.9175L75.3732 16.3009C74.6383 16.8578 73.8854 17.2888 73.1146 17.5942ZM68.275 6.89757C68.7232 6.70001 69.2072 6.60119 69.727 6.60119C70.1929 6.60119 70.6501 6.70898 71.0983 6.92452C71.5642 7.12211 71.9675 7.44544 72.3081 7.8945C72.6665 8.32559 72.9536 8.88242 73.1686 9.56502C73.3836 10.2476 73.4912 11.0559 73.4912 11.9899H65.9627C65.9807 11.02 66.0882 10.1937 66.2855 9.51113C66.5005 8.82855 66.7694 8.27169 67.092 7.8406C67.4326 7.40951 67.8269 7.09517 68.275 6.89757ZM88.1426 7.2748C86.8518 7.2748 85.8303 7.67894 85.0773 8.48725C84.3425 9.27757 83.975 10.4901 83.975 12.1246C83.975 13.7593 84.3425 14.9807 85.0773 15.789C85.8303 16.5793 86.8518 16.9745 88.1426 16.9745H91.503V7.2748H88.1426ZM95.2675 19.13C95.2675 19.9742 95.1059 20.7376 94.7836 21.4202C94.4612 22.1208 93.9773 22.7225 93.3317 23.2254C92.7043 23.7284 91.9071 24.1146 90.9392 24.384C89.9889 24.6534 88.8775 24.7882 87.6047 24.7882C87.1747 24.7882 86.7085 24.7524 86.2067 24.6804C85.7227 24.6264 85.2298 24.5277 84.7279 24.384C84.2259 24.2583 83.7331 24.0876 83.2492 23.8721C82.7829 23.6745 82.3528 23.441 81.9586 23.1715L82.8995 21.555C83.509 21.9681 84.1991 22.2914 84.9698 22.5249C85.7405 22.7764 86.5292 22.9022 87.3359 22.9022C88.6802 22.9022 89.7109 22.5967 90.428 21.986C91.1444 21.3753 91.503 20.4233 91.503 19.13V18.8606H88.1426C86.9058 18.8606 85.7764 18.6989 84.7548 18.3756C83.7509 18.0522 82.8904 17.5942 82.1737 17.0015C81.4566 16.4087 80.9009 15.6992 80.5066 14.8729C80.1302 14.0466 79.942 13.1306 79.942 12.1246C79.942 11.1188 80.1302 10.2026 80.5066 9.37636C80.9009 8.55013 81.4566 7.8406 82.1737 7.24784C82.8904 6.65509 83.7509 6.19704 84.7548 5.87372C85.7764 5.55039 86.9058 5.38873 88.1426 5.38873H95.2675V19.13ZM106.414 7.2748H109.641V5.38873H105.608C103.438 5.38873 101.772 5.90067 100.606 6.92452C99.4596 7.93043 98.8858 9.34946 98.8858 11.1817V19.3994H102.65V11.1817C102.65 9.88837 102.973 8.91835 103.618 8.2717C104.263 7.60711 105.195 7.2748 106.414 7.2748ZM115.68 19.3994H111.915V5.65816H115.68V19.3994ZM115.814 2.02078C115.814 2.55964 115.626 3.03564 115.249 3.4488C114.892 3.84397 114.407 4.04156 113.797 4.04156C113.188 4.04156 112.695 3.84397 112.318 3.4488C111.961 3.03564 111.781 2.55964 111.781 2.02078C111.781 1.4819 111.961 1.01487 112.318 0.619703C112.695 0.206568 113.188 2.68252e-08 113.797 0C114.389 -2.60153e-08 114.873 0.206568 115.249 0.619705C115.626 1.01487 115.814 1.4819 115.814 2.02078ZM127.107 7.2748H130.469V17.5134H127.107C126.498 17.5134 125.933 17.4236 125.414 17.2439C124.912 17.0464 124.473 16.741 124.097 16.3279C123.738 15.9147 123.451 15.3848 123.236 14.7382C123.039 14.0915 122.941 13.3101 122.941 12.3941C122.941 11.478 123.039 10.6966 123.236 10.05C123.451 9.40335 123.738 8.87346 124.097 8.46031C124.473 8.04718 124.912 7.7508 125.414 7.57117C125.933 7.37357 126.498 7.2748 127.107 7.2748ZM123.72 18.9145C124.742 19.2378 125.871 19.3994 127.107 19.3994H134.233V0.538871H130.469V5.38873H127.107C125.871 5.38873 124.742 5.55039 123.72 5.87372C122.717 6.19704 121.856 6.66407 121.138 7.2748C120.422 7.88551 119.866 8.62197 119.471 9.48414C119.096 10.3464 118.908 11.3164 118.908 12.3941C118.908 13.4718 119.096 14.4418 119.471 15.304C119.866 16.1662 120.422 16.9027 121.138 17.5134C121.856 18.1241 122.717 18.5911 123.72 18.9145Z'
              fill='white'
            />
            <path
              d='M152.5 9V3C152.5 1.89543 153.395 1 154.5 1H161C162.105 1 163 1.89543 163 3V10C163 11.1046 162.105 12 161 12H151.5H144C142.895 12 142 12.8954 142 14V21.5C142 22.6046 142.895 23.5 144 23.5H150.5C151.605 23.5 152.5 22.6046 152.5 21.5V15.25'
              stroke='white'
              strokeWidth='2'
            />
            <path
              d='M75.3122 34.2237C75.0974 34.2237 74.1139 34.2237 73.6957 34.2237V36.8576C74.227 36.8576 74.7922 36.8576 75.3122 36.8576C76.2843 36.8576 77.1774 36.4733 77.1774 35.6367C77.1774 34.642 76.2957 34.2237 75.3122 34.2237ZM75.3122 30.9793C75.2557 30.9793 73.6957 30.9793 73.6957 30.9793V33.2628C74.1139 33.2628 75.0974 33.2628 75.3122 33.2628C76.2957 33.2628 76.8383 32.9237 76.8383 32.0533C76.8383 31.375 76.2843 30.9793 75.3122 30.9793ZM72.5652 29.962C73.1757 29.962 73.7974 29.962 74.4191 29.962C74.713 29.962 75.0183 29.962 75.3122 29.962C77.3696 29.962 78.0478 30.9341 78.0478 32.0193C78.0478 32.7315 77.6861 33.195 77.053 33.6359C78.2061 34.2689 78.3983 34.868 78.3983 35.6028C78.3983 36.835 77.46 37.875 75.3122 37.875C75.0748 37.875 74.8374 37.875 74.6 37.875C74.0687 37.875 73.5487 37.875 72.9948 37.875C72.8478 37.875 72.7122 37.875 72.5652 37.875V29.962ZM82.1239 29.962H83.3109V35.0602C83.3109 36.1907 83.9779 36.8463 85.1196 36.8463H87.0753V37.875H85.1196C83.4126 37.875 82.1239 36.9593 82.1239 35.0602V29.962ZM93.4951 31.0246C92.0255 31.0246 90.8838 32.2793 90.8838 33.9298C90.8838 35.5915 92.0255 36.8463 93.4951 36.8463C94.976 36.8463 96.1177 35.5915 96.1177 33.9298C96.1177 32.2793 94.976 31.0246 93.4951 31.0246ZM93.4951 29.8602C95.6316 29.8602 97.3386 31.7141 97.3386 33.9298C97.3386 36.1567 95.6316 37.9993 93.4951 37.9993C91.3586 37.9993 89.6629 36.1567 89.6629 33.9298C89.6629 31.7141 91.3586 29.8602 93.4951 29.8602ZM104.711 29.8602C105.841 29.8602 106.531 30.2333 107.198 30.9793L106.474 31.7254C105.988 31.2507 105.547 30.9228 104.711 30.9228C103.23 30.9228 102.077 32.4489 102.077 33.9298C102.077 35.4107 103.23 36.9367 104.711 36.9367C105.468 36.9367 106.203 36.5863 106.666 35.9985L107.503 36.7559C106.825 37.5246 105.853 37.9993 104.711 37.9993C102.473 37.9993 100.89 36.168 100.89 33.9298C100.89 31.6915 102.473 29.8602 104.711 29.8602ZM111.181 29.962H112.368V34.122L115.839 29.962H117.15L114.459 33.2289L117.557 37.875H116.099L113.646 34.1898L112.368 35.7611V37.875H111.181V29.962ZM123.826 29.8602C124.956 29.8602 125.646 30.2333 126.313 30.9793L125.589 31.7254C125.103 31.2507 124.662 30.9228 123.826 30.9228C122.345 30.9228 121.192 32.4489 121.192 33.9298C121.192 35.4107 122.345 36.9367 123.826 36.9367C124.583 36.9367 125.318 36.5863 125.781 35.9985L126.618 36.7559C125.94 37.5246 124.967 37.9993 123.826 37.9993C121.587 37.9993 120.005 36.168 120.005 33.9298C120.005 31.6915 121.587 29.8602 123.826 29.8602ZM130.296 29.962H131.483V33.1385H135.417V29.962H136.604V37.875H135.417V34.0993H131.483V37.875H130.296V29.962ZM143.89 30.855C142.884 30.855 141.867 31.4202 141.867 32.788V34.6985H145.801V32.788C145.801 31.6124 145.066 30.8663 143.89 30.855ZM140.737 32.788C140.737 31.4315 141.709 29.8489 143.89 29.8489C146.05 29.8489 146.988 31.2054 146.988 32.788V37.875H145.801V35.6593H141.867V37.875H140.737V32.788ZM151.166 37.875V29.962H152.353V37.875H151.166ZM156.484 29.962H157.683L161.718 36.0211V29.962H162.792V37.875H161.605L157.57 31.8159V37.875H156.484V29.962Z'
              fill='white'
            />
          </svg>

          {/* <img src={logoWordmark} alt='tradegrid' className='admin-sidebar__brand-wordmark' />
          <img src={logoMark} alt='' className='admin-sidebar__brand-mark' /> */}
        </div>
        {/* <div className='admin-sidebar__brand-sub'>BLOCKCHAIN</div> */}
      </div>

      <nav className='admin-sidebar__nav' aria-label='Admin Navigation'>
        <div className='admin-sidebar__section'>
          <div className='admin-sidebar__section-title'>OVERVIEW</div>
          <Link
            to={toForKey('dashboard')}
            className={[
              'admin-sidebar__item',
              isDashboardActive ? 'admin-sidebar__item--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onNavigate?.()}
          >
            <span className='admin-sidebar__home-icon'>
              <img src={iconHome} alt='' className='admin-sidebar__home-subtract' />
              <span className='admin-sidebar__home-roof' />
            </span>
            <span
              className={[
                'admin-sidebar__item-label',
                isDashboardActive ? 'admin-sidebar__item-label--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              Dashboard
            </span>
          </Link>
        </div>

        <div className='admin-sidebar__section'>
          <div className='admin-sidebar__section-title'>USERS</div>
          <SidebarItem
            itemKey='kyc-queue'
            label='Legal'
            icon={iconMenu}
            to={toForKey('kyc-queue')}
            activeKey={activeKey}
            onNavigate={onNavigate}
          />
          <SidebarItem
            itemKey='investors'
            label='Investors'
            icon={iconMenu}
            to={toForKey('investors')}
            activeKey={activeKey}
            onNavigate={onNavigate}
          />
        </div>

        <div className='admin-sidebar__section'>
          <div className='admin-sidebar__section-title'>STATION</div>
          <SidebarItem
            itemKey='traders-card'
            label='TradersCard'
            icon={iconMenu}
            to={toForKey('traders-card')}
            activeKey={activeKey}
            onNavigate={onNavigate}
          />
        </div>

        <div className='admin-sidebar__section'>
          <div className='admin-sidebar__section-title'>FINANCE</div>
          <SidebarItem
            itemKey='pool-state'
            label='Pool State'
            icon={iconMenu}
            to={toForKey('pool-state')}
            activeKey={activeKey}
            onNavigate={onNavigate}
          />
          <SidebarItem
            itemKey='token-assets'
            label='Token Assets'
            icon={iconMenu}
            to={toForKey('token-assets')}
            activeKey={activeKey}
            onNavigate={onNavigate}
          />
        </div>

        {/* <div className='admin-sidebar__section'>
          <div className='admin-sidebar__section-title'>OPERATIONS</div>
          <SidebarItem
            itemKey='notifications'
            label='Notifications'
            icon={iconMenu}
            to={toForKey('notifications')}
            activeKey={activeKey}
            onNavigate={onNavigate}
          />
        </div> */}

        <div className='admin-sidebar__section'>
          <div className='admin-sidebar__section-title'>ACCOUNT</div>
          <button
            type='button'
            className='admin-sidebar__item admin-sidebar__item--button'
            onClick={() => {
              setIsSwitching(true)
              window.setTimeout(() => {
                navigate('/dashboard', { replace: true })
              }, 450)
            }}
          >
            <img src={iconMenu} alt='' className='admin-sidebar__item-icon' />
            <span className='admin-sidebar__item-label'>Main Admin</span>
          </button>
          <button
            type='button'
            className='admin-sidebar__item admin-sidebar__item--button'
            data-bs-toggle='modal'
            data-bs-target='#adminLogoutConfirmModal'
          >
            <img src={iconMenu} alt='' className='admin-sidebar__item-icon' />
            <span className='admin-sidebar__item-label'>Logout</span>
          </button>
        </div>
      </nav>

      <OverlayTrigger
        trigger='click'
        placement='top'
        overlay={
          <Popover id='popover-positioned-top'>
            <Popover.Body>
              <button type='button' className='btn btn-link text-danger' onClick={() => logout()}>
                Logout
              </button>
            </Popover.Body>
          </Popover>
        }
      >
        <div className='admin-sidebar__profile'>
          <img src={iconProfile} alt='' className='admin-sidebar__profile-icon' />
          <div className='admin-sidebar__profile-text'>
            <div className='admin-sidebar__profile-name'>{user?.user.name}</div>
            <div className='admin-sidebar__profile-role'>Super Admin</div>
          </div>
          <img src={iconChevronDown} alt='' className='admin-sidebar__profile-chevron' />
        </div>
      </OverlayTrigger>

      <div
        className='modal fade'
        id='adminLogoutConfirmModal'
        tabIndex={-1}
        aria-labelledby='adminLogoutConfirmModalLabel'
        aria-hidden='true'
      >
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title' id='adminLogoutConfirmModalLabel'>
                Confirm Logout
              </h5>
              <button
                type='button'
                className='btn-close'
                data-bs-dismiss='modal'
                aria-label='Close'
              />
            </div>
            <div className='modal-body'>Are you sure you want to logout?</div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-outline-secondary' data-bs-dismiss='modal'>
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-primary'
                data-bs-dismiss='modal'
                onClick={() => logout()}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {isSwitching && (
        <div className='admin-switching-overlay' role='status' aria-live='polite'>
          <div className='admin-switching-overlay__card'>
            <div className='spinner-border' aria-hidden='true' />
            <div className='admin-switching-overlay__text'>Switching, please wait...</div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminSidebar
